import React from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut' },
  }),
};

const features = [
  {
    icon: '🏨',
    title: 'Multi-Vendor Aggregation',
    desc: 'We pull real-time pricing from multiple hotel vendors so you never miss a better deal.',
  },
  {
    icon: '💰',
    title: 'Transparent Pricing',
    desc: 'See vendor-wise pricing side by side for the same property — no hidden fees, no surprises.',
  },
  {
    icon: '⚡',
    title: 'Instant Comparison',
    desc: 'Compare rates across all vendors in seconds. Save time and maximize your margins.',
  },
  {
    icon: '🤝',
    title: 'B2B Focused',
    desc: 'Built specifically for travel agents and businesses who need reliable, scalable hotel booking tools.',
  },
  {
    icon: '🔒',
    title: 'Secure & Reliable',
    desc: 'Enterprise-grade security with 99.9% uptime. Your data and transactions are always protected.',
  },
  {
    icon: '📊',
    title: 'Smart Analytics',
    desc: 'Track bookings, spending, and trends with built-in reporting tools designed for business users.',
  },
];

const About = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">
            About Us
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Built for the Modern{' '}
            <span className="text-blue-600">Travel Business</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We are a B2B hotel aggregation platform designed to simplify how businesses search and book accommodations.
          </p>
        </motion.div>

        {/* Main Description */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20"
        >
          <div className="space-y-5 text-gray-600 text-base leading-relaxed">
            <p>
              <strong className="text-gray-900">Filter Hotels</strong> is a B2B hotel aggregation platform designed to simplify how businesses search and book accommodations. We aggregate hotel inventory from multiple vendors and display vendor-wise pricing for the same property — all in one place.
            </p>
            <p>
              Our platform is built for travel agents, corporate travel managers, and hospitality businesses who need a fast, reliable, and transparent way to compare hotel rates. Instead of checking multiple booking portals manually, Filter Hotels does the heavy lifting for you.
            </p>
            <p>
              We believe that every business deserves access to the best hotel rates without the complexity. Our technology connects you directly to a wide network of hotel vendors, giving you real-time pricing, availability, and booking capabilities — all from a single dashboard.
            </p>
            <p>
              Whether you're managing bookings for a single client or handling hundreds of reservations per month, Filter Hotels scales with your business. Our platform is designed to save you time, reduce costs, and improve the overall booking experience for your clients.
            </p>
          </div>

          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070&auto=format&fit=crop"
                alt="Hotel lobby"
                className="w-full h-80 object-cover"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white rounded-2xl p-5 shadow-xl">
              <div className="text-3xl font-bold">50+</div>
              <div className="text-blue-200 text-sm">Hotel Vendors</div>
            </div>
            <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-gray-100">
              <div className="text-3xl font-bold text-gray-900">10K+</div>
              <div className="text-gray-500 text-sm">Properties</div>
            </div>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={idx * 0.1}
              className="group p-6 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 bg-white"
            >
              <div className="text-3xl mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
